import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import './Admin.css';
import { Markup } from './types';
import MarkupModal from './MarkupModal';

const Markups = () => {
    const [markups, setMarkups] = useState<Markup[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [editingMarkup, setEditingMarkup] = useState<Markup | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);

    useEffect(() => {
        fetchMarkups();
    }, []);

    const fetchMarkups = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/get-markups`, { 
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                    "Content-Type": "application/json",
                },
                method: 'GET',
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch markups');
            }
            
            const data = await response.json();
            setMarkups(data.markups || []);
        } catch (error) {
            console.error('Error fetching markups:', error);
            toast.error('Failed to load markups', { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (markup: Markup) => {
        try {
            const url = editingMarkup 
                ? `${process.env.REACT_APP_ADMIN_API_URL}/api/update-markup/${editingMarkup.sceneName}`
                : `${process.env.REACT_APP_ADMIN_API_URL}/api/create-markup`;
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify(markup),
            });

            if (!response.ok) {
                throw new Error('Failed to save markup');
            }

            toast.success(`Markup ${editingMarkup ? 'updated' : 'created'} successfully`, 
                { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            
            fetchMarkups();
        } catch (error) {
            console.error('Error saving markup:', error);
            toast.error('Failed to save markup', { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        }
    };

    const handleEdit = (markup: Markup) => {
        setEditingMarkup(markup);
        setShowModal(true);
    };

    const handleDelete = async (sceneName: string) => {
        if (!window.confirm('Are you sure you want to delete this markup?')) {
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/delete-markup/${sceneName}`, {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete markup');
            }

            toast.success('Markup deleted successfully', { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            fetchMarkups();
        } catch (error) {
            console.error('Error deleting markup:', error);
            toast.error('Failed to delete markup', { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        }
    };

    return (
        <div className="markups-container">
            <ToastContainer/>
            <div className="markups-header">
                <h2>Markups Management</h2>
                <button 
                    className="add-button"
                    onClick={() => {
                        setEditingMarkup(null);
                        setShowModal(true);
                    }}
                >
                    Add New Markup
                </button>
            </div>

            {isLoading ? (
                <div className="loading">Loading markups...</div>
            ) : (
                <div className="markups-list">
                    {markups.map(markup => (
                        <div key={markup.hash} className="markup-card">
                            <div className="markup-header">
                                <h3>{markup.sceneName}</h3>
                                <div className="markup-actions">
                                    <button 
                                        className="edit-button"
                                        onClick={() => handleEdit(markup)}
                                    >
                                        Edit
                                    </button>
                                    <button 
                                        className="delete-button"
                                        onClick={() => handleDelete(markup.sceneName)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <MarkupModal
                    markup={editingMarkup}
                    onClose={() => {
                        setShowModal(false);
                        setEditingMarkup(null);
                    }}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default Markups; 